import { moduleContent as ProjectContentModule } from "../Components/ProjectContent";
import { getListOfAllModules } from "../Utils/modules";
import {
    getDisplayNameByModuleInstanceId,
    getModuleInstanceInfo
} from "../Components/ProjectContent/Utils/projectInfo";

export interface IPathRouting {
    displayName?: string;
    id?: string;
    moduleName: string;
    moduleLinkedId?: string;
}

class PathRouting {
    private static _instance: PathRouting;

    private _currentProject: IPathRouting | undefined = undefined;
    private _currentModule: IPathRouting | undefined = undefined;
    private _currentPage: IPathRouting | undefined = undefined;

    private _observers: (() => void)[] = [];

    private constructor() {
    }

    public static get Instance(): PathRouting {
        // Only create once for the class itself (not for global use)
        return this._instance || (this._instance = new this());
    }

    public subscribe(observer: () => void) {
        this._observers.push(observer);
    }

    public unsubscribe(observer: () => void) {
        this._observers = this._observers.filter(obs => obs !== observer);
    }

    private _notify() {
        this._observers.forEach(observer => observer());
        this.setBrowserTabTitle();
    }

    get currentProject(): IPathRouting | undefined {
        return this._currentProject;
    }
    set currentProject(value: IPathRouting | undefined) {
        this._currentProject = value;
        if (value && value.id && !value.displayName) {
            getDisplayNameByModuleInstanceId(value.id).then(displayName => {
                if (this._currentProject) {
                    this._currentProject.displayName = displayName;
                    this._notify();
                }
            });
        }
        this._notify();
    }

    /**
     * Sets the current project based on the URL
     */
    public setProject() {
        let url = window.location.href;
        const moduleInstanceId = url.split("/")[5];
        if (!moduleInstanceId) {
            this.currentProject = { id: "", moduleName: "" };
        } else {
            this.currentProject = { id: moduleInstanceId, moduleName: ProjectContentModule.moduleName };
        }
    }

    get currentModule(): IPathRouting | undefined {
        return this._currentModule;
    }
    set currentModule(value: IPathRouting | undefined) {
        this._currentModule = value;
        if (
            value &&
            value.id &&
            !value.displayName &&
            this._currentProject &&
            this._currentProject.id
        ) {
            getModuleInstanceInfo(this._currentProject.id, value.id).then(module => {
                if (this._currentModule) {
                    this._currentModule.displayName = module.displayName;
                    this._currentModule.moduleLinkedId = module.linkedId?.projectId;
                    this._notify();
                }
            });
        }
        this._notify();
    }

    public setModule() {
        let url = window.location.href;
        const moduleInstanceId = url.split("/")[7]?.split("?")[0] || "";
        const moduleName = url.split("/")[6] || "";
        const linkedID = url.includes("linkedId=") && url.split("?")[1]
            ? url.split("?")[1].split("linkedId=")[1]
            : undefined;
        const modules = getListOfAllModules();
        if (!(moduleName in modules)) {
            console.error("ERROR: module " + moduleName + " not found");
        }
        this.currentModule = { id: moduleInstanceId, moduleName: moduleName, moduleLinkedId: linkedID };
    }

    get currentPage(): IPathRouting | undefined {
        return this._currentPage;
    }
    set currentPage(value: IPathRouting | undefined) {
        this._currentPage = value;
        this._notify();
    }

    public clearProject() {
        this._currentProject = undefined;
        this._notify();
    }

    public clearModule() {
        this._currentModule = undefined;
        this._notify();
    }

    public clearProjectAndModule() {
        this._currentProject = undefined;
        this._currentModule = undefined;
        this._notify();
    }

    public setBrowserTabTitle() {
        let projectName = "";
        if (this._currentProject?.displayName) {
            projectName = this._currentProject.displayName + " - ";
        }
        if (this._currentModule?.displayName) {
            projectName += this._currentModule.displayName + " - ";
        }
        if (this._currentPage?.displayName) {
            projectName += this._currentPage.displayName;
        }
        document.title = projectName;
    }
}

// Original Problem: DPAS couldnt use the module ID after reload. This likely happened due to 2 instances of hubpathrouting existing at the same time. The code below was written by GAIA, we dont know why, but it works.

// ---- SINGLETON GLOBAL PROTECTION ----
const SINGLETON_KEY = "__HubPathRoutingInstance__";

declare global {
    // eslint-disable-next-line no-var
    var __HubPathRoutingInstance__: PathRouting | undefined;
}

if (!globalThis[SINGLETON_KEY]) {
    // @ts-ignore
    globalThis[SINGLETON_KEY] = PathRouting.Instance;
}

export const HubPathRouting: PathRouting = (globalThis as any)[SINGLETON_KEY];
